import React, { useState } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { useTheme } from '@mui/material/styles'
import { gridClasses } from '@mui/x-data-grid-pro'
import { GridColDef } from '@mui/x-data-grid/models'
import { NotebookPenIcon } from 'lucide-react'
import { FullHeightBox } from '../UIComponents'
import { NoteEditorDialog } from '../../pages/components/NoteEditorDialog'
import { EntityNoteType } from '../../pages/components/EntityNoteButton'
import { NoteEntityRef } from '../../api/NotesApi'
import { IssueGridCols } from '@finance-lab/utils/types'

const toEntityRef = (entityId: string, type: EntityNoteType): NoteEntityRef => {
    if (type === 'issue') return { issueId: entityId }
    if (type === 'warrant') return { warrantId: entityId }
    return { convertibleId: entityId }
}

export const noteIndicatorGridSx = {
    [`& .${gridClasses.cell}[data-field="noteIndicator"]`]: {
        paddingLeft: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    [`& .${gridClasses.columnHeader}[data-field="noteIndicator"]`]: {
        paddingLeft: 0,
    },
}

export const NoteRowIndicator = ({
    entityId,
    onNotesChanged,
    type,
}: {
    entityId: string
    onNotesChanged?: () => void
    type: EntityNoteType
}) => {
    const theme = useTheme()
    const [open, setOpen] = useState(false)
    const entityRef = toEntityRef(entityId, type)

    return (
        <>
            <Tooltip title='Anteckning'>
                <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    <IconButton
                        aria-label='Anteckning'
                        onClick={(event) => {
                            event.stopPropagation()
                            setOpen(true)
                        }}
                        size='small'
                        sx={{ padding: 0.5 }}
                    >
                        <NotebookPenIcon
                            color={theme.palette.primary.main}
                            fill={theme.palette.primary.main}
                            size={18}
                        />
                    </IconButton>
                </Box>
            </Tooltip>
            <NoteEditorDialog
                entityRef={entityRef}
                onClose={() => setOpen(false)}
                onDeleted={() => {
                    onNotesChanged?.()
                }}
                onSaved={() => {
                    onNotesChanged?.()
                }}
                open={open}
            />
        </>
    )
}

export const createNoteIndicatorColumn = (
    entityIdsWithNotes: Set<string>,
    type: EntityNoteType,
    onNotesChanged?: () => void,
): GridColDef & { field: IssueGridCols } => ({
    field: 'noteIndicator',
    headerName: '',
    width: 36,
    minWidth: 36,
    maxWidth: 36,
    sortable: false,
    disableColumnMenu: true,
    resizable: false,
    display: 'flex',
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) =>
        entityIdsWithNotes.has(params.row.id) ? (
            <FullHeightBox>
                <NoteRowIndicator entityId={params.row.id} onNotesChanged={onNotesChanged} type={type} />
            </FullHeightBox>
        ) : null,
})
